import React, { useState } from "react";
import Button from "../../../Components/Common/Button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faAdd,
  faArrowLeft,
  faEdit,
  faFolderOpen,
  faSearch,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import Input from "../../../Components/Common/Input";
import { FileApiUrls } from "../../../Api/Auth/ApiUrls";
import useSWR from "swr";
import { Loader } from "../../../Components/Common/Loader";
import PaginationControls from "../../../Components/Common/Pagination/pagination/PaginationControls";
import ImagePreview from "./Components/ImagePreview";
import UploadImage from "./Components/UploadImage";
import Modal from "../../../Components/Common/Modal";
import useSWRMutation from "swr/mutation";
import {
  sendDeleteRequestAuth,
  sendPostRequestAuth,
  sendPutRequestAuth,
} from "../../../Api/swrConfig";
import { toast } from "react-toastify";
import useToastError from "../../../hooks/useErrorToast";
import { useAlert } from "../../../Context/AlertContext";

const LIMIT = 18; // Items per page

const Storage = ({
  onConfirm,
  multiple = false,
  isSelector = false,
  onChange,
}) => {
  const [offset, setOffset] = useState(0); // Current offset for pagination
  const [searchTerm, setSearchTerm] = useState(""); // Holds input value
  const [search, setSearch] = useState(""); // Search query
  const [selectedFile, setSelectedFile] = useState(null);
  const [imageLoading, setImageLoading] = useState({});
  const [showAdd, setShowAdd] = useState(false);
  const [selectedImages, setSelectedImages] = useState([]);
  const [folder, setFolder] = useState("");
  const [isFolderModalOpen, setFolderModalOpen] = useState(false);
  const [edit, setEdit] = useState(false);
  const [errors, setErrors] = useState({});
  const { showErrorToast } = useToastError();
  const { showAlert } = useAlert();

  const [formData, setFormData] = useState({
    folder: "",
  });

  const handleFormInputChange = (name, value) => {
    setFormData((prevState) => ({
      ...prevState,
      [name]: value, // Set only the field that's changed
    }));
  };

  const { isLoading, data, mutate } = useSWR(
    FileApiUrls.list(
      `offset=${offset}&limit=${LIMIT}&fileName=${search}&folder=${folder}`
    )
  );

  const { trigger, isMutating } = useSWRMutation(
    FileApiUrls.createFolder,
    sendPostRequestAuth,
    {
      onSuccess: (data) => {
        toast.success("Success");
        mutate();
        setFolderModalOpen(false);
      },
      onError: (error) => {
        showErrorToast(error);
      },
    }
  );
  const { trigger: renameTrigger, isMutating: renameMutating } = useSWRMutation(
    FileApiUrls.createFolder,
    sendPutRequestAuth,
    {
      onSuccess: (data) => {
        toast.success("Success");
        mutate();
        setFolderModalOpen(false);
        setFolder(formData.folder);
        setEdit(false);
      },
      onError: (error) => {
        showErrorToast(error);
      },
    }
  );

  // Extract pagination details
  const pagination = data?.pagination || {};
  const totalPages = Math.ceil(pagination.totalItems / LIMIT);
  const currentPage = Math.floor(offset / LIMIT) + 1;
  const currentFolderLength =
    data?.data?.filter((file) => file.contentType !== "folder")?.length || 0;

  // Handle Pagination
  const goToPage = (page) => setOffset((page - 1) * LIMIT);
  const goNext = () => pagination.hasNextPage && setOffset(offset + LIMIT);
  const goPrev = () => pagination.hasPrevPage && setOffset(offset - LIMIT);

  // Handle image loading state
  const handleImageLoad = (id) => {
    setImageLoading((prevState) => ({ ...prevState, [id]: true }));
  };

  // Handle Search Input
  const handleInputChange = (_, value) => {
    setSearchTerm(value);
  };

  const handleSearch = () => {
    // if (searchTerm.trim() !== "") {
    setImageLoading({});
    setSearch(searchTerm); // Set search query for API call
    setOffset(0); // Reset pagination to first page
    // }
  };
  // Trigger search on Enter key press
  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };
  const handleImageClick = (file) => {
    setSelectedFile(file); // Set the selected file for preview
  };
  // Handle Image Click (Preview)
  const handleImageCheckClick = (file) => {
    setSelectedImages((prev) => {
      const isAlreadySelected = prev.some((f) => f._id === file._id);

      if (multiple) {
        return isAlreadySelected
          ? prev.filter((f) => f._id !== file._id) // Deselect if selected
          : [...prev, file]; // Select otherwise
      } else {
        return isAlreadySelected ? [] : [file]; // Allow deselection in single mode
      }
    });
  };

  // Handle Back to Grid
  const handleBackToGrid = () => {
    mutate();
    setSelectedFile(null); // Clear the selected file and go back to the grid view
  };

  const handleCheckboxChange = (file) => {
    handleImageCheckClick(file);
  };

  const { trigger: deleteTrigger } = useSWRMutation(
    FileApiUrls.deleteFolder(folder),
    sendDeleteRequestAuth,
    {
      onSuccess: () => {
        toast.success("Success");
        mutate();
        setFolder("");
      },
      // If error occurred - make delete false
      onError: (error) => {
        showErrorToast(error);
      },
    }
  );

  const handleDelete = () => {
    showAlert(
      `Are you sure you want to delete the folder "${folder}"?`,
      async () => {
        await deleteTrigger();
      }
    );
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.folder) newErrors.folder = "Folder Name is required";
    // Add more validation logic as needed
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    const editData = {
      oldName: folder,
      newName: formData.folder,
    };
    edit ? renameTrigger(editData) : trigger(formData);
  };

  // Confirm selected images
  const handleConfirm = () => {
    if (onConfirm) onConfirm(selectedImages);
  };

  if (showAdd) {
    return (
      <UploadImage
        onBack={() => {
          setShowAdd(false);
          handleBackToGrid();
        }}
        folder={folder}
      />
    );
  }
  if (selectedFile) {
    return (
      <ImagePreview
        file={selectedFile}
        onBack={handleBackToGrid}
        folder={folder}
      />
    );
  }

  return (
    <div>
      {/* Top Controls */}
      <div className="flex sm:justify-between flex-wrap gap-2 mb-4">
        <div className="flex gap-2">
          <Button
            children={
              <span className="flex gap-2 items-center">
                <FontAwesomeIcon icon={faAdd} />
                Add File
              </span>
            }
            onClick={() => {
              setShowAdd(true);
            }}
            size="small"
          />
          {!folder && (
            <Button
              children={
                <span className="flex gap-2 items-center">
                  <FontAwesomeIcon icon={faAdd} />
                  Add Folder
                </span>
              }
              onClick={() => {
                setFormData({
                  folder: "",
                });
                setFolderModalOpen(true);
              }}
              size="small"
              type="outline"
            />
          )}
          {folder && (
            <Button
              type="outline"
              size="small"
              onClick={() => {
                setEdit(true);
                handleFormInputChange("folder", folder);
                setFolderModalOpen(true);
              }}
            >
              <span className="flex gap-2 items-center">
                <FontAwesomeIcon icon={faEdit} />
                Rename
              </span>
            </Button>
          )}
          {folder && (
            <Button type="danger" size="small" onClick={() => handleDelete()}>
              <span className="flex gap-2 items-center">
                <FontAwesomeIcon icon={faTrash} />
                Delete
              </span>
            </Button>
          )}
          {folder && (
            <Button
              type="outline"
              size="small"
              onClick={() => {
                setFolder("");
                setOffset(0);
                mutate(); // Reload the root
              }}
            >
              <span className="flex gap-2 items-center">
                <FontAwesomeIcon icon={faArrowLeft} />
                Back to List
              </span>
            </Button>
          )}
        </div>

        <div className="flex gap-1">
          <Input
            name="search"
            placeholder="Search for files"
            onChange={handleInputChange}
            onKeyDown={handleKeyPress}
            value={searchTerm}
          />
          <Button
            children={<FontAwesomeIcon icon={faSearch} />}
            size="icon"
            onClick={handleSearch}
          />
        </div>
      </div>
      {folder && <h2 className="font-medium mb-2">{folder}</h2>}
      {/* File Grid */}
      {isLoading ? (
        <Loader />
      ) : data?.data?.length > 0 ? (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
            {folder === "" ? (
              <>
                {/* Show unique folders */}
                {data?.data
                  ?.filter(
                    (file, index, self) =>
                      file.folder &&
                      index === self.findIndex((f) => f.folder === file.folder)
                  )
                  .map((file) => (
                    <div
                      key={file.folder}
                      className="relative group w-full aspect-square max-w-[165px] cursor-pointer flex items-center justify-center rounded-lg shadow-md"
                      onClick={() => {
                        setSearch("");
                        setOffset(0);
                        setFolder(file.folder);
                        mutate();
                      }}
                    >
                      <span className="text-primary font-semibold text-sm text-center px-2 flex flex-col">
                        <FontAwesomeIcon
                          icon={faFolderOpen}
                          className="text-8xl"
                        />
                        {file.folder}
                      </span>
                    </div>
                  ))}

                {/* Show files in root (folder === "") */}
                {data?.data
                  ?.filter((file) => file.folder === "")
                  .map((file) => {
                    const isSelected = selectedImages.some(
                      (f) => f._id === file._id
                    );
                    const isDisabled =
                      !multiple && selectedImages.length > 0 && !isSelected;

                    return (
                      <div
                        key={file._id}
                        className="relative group w-full aspect-square max-w-[165px] cursor-pointer"
                        onClick={() => handleImageClick(file)}
                      >
                        {isSelector && (
                          <input
                            type="checkbox"
                            className="absolute top-2 left-2 w-5 h-5 z-10 bg-white rounded-md cursor-pointer"
                            checked={isSelected}
                            disabled={isDisabled}
                            onChange={() => handleCheckboxChange(file)}
                            onClick={(e) => e.stopPropagation()}
                          />
                        )}
                        <div className="relative overflow-hidden w-full h-full rounded-lg shadow-md">
                          <div
                            className={`w-full h-full bg-gray-300 ${
                              !imageLoading[file._id]
                                ? "animate-pulse"
                                : "hidden"
                            }`}
                          ></div>
                          {file.contentType.includes("video") ? (
                            <img
                              src="/videoThumb.jpg"
                              alt={file.originalName}
                              className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-300"
                              onLoad={() => handleImageLoad(file._id)}
                            />
                          ) : file.contentType === "application/pdf" ? (
                            <img
                              src="https://storage.googleapis.com/road-to-fe/uploads/1741110111744-5fb0db0b-ee21-4a9e-82f4-f09d87b3bbc6-pdfIcon.png"
                              alt={file.originalName}
                              className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-300"
                              onLoad={() => handleImageLoad(file._id)}
                            />
                          ) : (
                            <img
                              src={file.fileUrl}
                              alt={file.originalName}
                              className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-300"
                              onLoad={() => handleImageLoad(file._id)}
                            />
                          )}
                        </div>

                        <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                          <span className="text-white text-xs text-center px-2">
                            {file.originalName}
                          </span>
                        </div>
                      </div>
                    );
                  })}
              </>
            ) : (
              // Show all files in the selected folder
              data?.data
                ?.filter((item) => {
                  return item.contentType !== "folder";
                })
                .map((file) => {
                  const isSelected = selectedImages.some(
                    (f) => f._id === file._id
                  );
                  const isDisabled =
                    !multiple && selectedImages.length > 0 && !isSelected;

                  return (
                    <div
                      key={file._id}
                      className="relative group w-full aspect-square max-w-[175px] cursor-pointer"
                      onClick={() => handleImageClick(file)}
                    >
                      {isSelector && (
                        <input
                          type="checkbox"
                          className="absolute top-2 left-2 w-5 h-5 z-10 bg-white rounded-md cursor-pointer"
                          checked={isSelected}
                          disabled={isDisabled}
                          onChange={() => handleCheckboxChange(file)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      )}
                      <div className="relative overflow-hidden w-full h-full rounded-lg shadow-md">
                        <div
                          className={`w-full h-full bg-gray-300 ${
                            !imageLoading[file._id] ? "animate-pulse" : "hidden"
                          }`}
                        ></div>
                        {file.contentType.includes("video") ? (
                          <img
                            src="/videoThumb.jpg"
                            alt={file.originalName}
                            className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-300"
                            onLoad={() => handleImageLoad(file._id)}
                          />
                        ) : file.contentType === "application/pdf" ? (
                          <img
                            src="https://storage.googleapis.com/road-to-fe/uploads/1741110111744-5fb0db0b-ee21-4a9e-82f4-f09d87b3bbc6-pdfIcon.png"
                            alt={file.originalName}
                            className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-300"
                            onLoad={() => handleImageLoad(file._id)}
                          />
                        ) : (
                          <img
                            src={file.fileUrl}
                            alt={file.originalName}
                            className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-300"
                            onLoad={() => handleImageLoad(file._id)}
                          />
                        )}
                      </div>

                      <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <span className="text-white text-xs text-center px-2">
                          {file.originalName}
                        </span>
                      </div>
                    </div>
                  );
                })
            )}
            {currentFolderLength === 0 && !isLoading && "No Items Found"}
          </div>

          {/* Pagination Controls */}
          <div className="flex justify-between items-center gap-2 mt-4">
            <PaginationControls
              onClick={goPrev}
              direction={-1}
              disabled={!pagination.hasPrevPage}
            />
            <div className="flex flex-wrap items-center gap-2 hidden sm:flex">
              {/* Page Numbers */}
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                (page) => (
                  <Button
                    key={page}
                    onClick={() => goToPage(page)}
                    size="small"
                    type={page === currentPage ? "primary" : "outline"}
                  >
                    {page}
                  </Button>
                )
              )}
            </div>

            <PaginationControls
              onClick={goNext}
              direction={1}
              disabled={!pagination.hasNextPage}
            />
          </div>
          {/* Confirm Button */}
          {selectedImages.length > 0 && (
            <div className="flex justify-center mt-4">
              <Button type="primary" onClick={handleConfirm}>
                Confirm ({selectedImages.length})
              </Button>
            </div>
          )}
        </>
      ) : (
        <p className="text-gray-500">No files uploaded yet.</p>
      )}
      <Modal
        isOpen={isFolderModalOpen}
        title={"Create Folder"}
        onConfirm={handleSubmit}
        onCancel={() => {
          setErrors({});
          setFolderModalOpen(false);
        }}
        isLoading={isMutating || renameMutating}
        formWidth
      >
        <form onSubmit={handleSubmit} className="flex flex-col gap-2">
          <Input
            label="Folder Name"
            name="folder"
            required
            placeholder="Enter Folder name"
            onChange={handleFormInputChange}
            error={errors.folder}
            value={formData.folder}
          />
        </form>
      </Modal>
    </div>
  );
};

export default Storage;
